import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useSuspenseQuery } from "@tanstack/react-query";
import { ArrowLeft, Calendar, User } from "lucide-react";

import { Section } from "@/components/site/section";
import { NewsCard, dateUtils } from "@/components/site/cards";
import { Img } from "@/components/site/img";
import { MatchFlags, getArgentinaMatchOpponent } from "@/components/site/flag";
import { resolveArticleHeroImage } from "@/assets/photos";
import { articleBySlugQuery, newsByCategoryQuery } from "@/lib/site-queries";

export const Route = createFileRoute("/news_/$slug")({
  head: ({ params, loaderData }) => {
    const a = (loaderData as { article?: { id?: string | null; title: string; excerpt: string | null; cover_image?: string | null; body?: string | null } } | undefined)?.article;

    const title = a?.title ?? "Story | Argentinian.Soccer";
    // Resolve via the same helper the component uses — preload and painted
    // <Img> always agree on the URL, so the preload always satisfies LCP.
    const img = resolveArticleHeroImage(a ?? null);
    return {
      meta: [
        { title: `${title} | Argentinian.Soccer` },
        { name: "description", content: a?.excerpt ?? "Story on Argentinian.Soccer." },
        { property: "og:title", content: title },
        { property: "og:url", content: `/news/${params.slug}` },
        { property: "og:type", content: "article" },
        { property: "og:image", content: img },
        { name: "twitter:image", content: img },
      ],
      links: [
        { rel: "canonical", href: `/news/${params.slug}` },
        { rel: "preload", as: "image", href: img, fetchpriority: "high" },
      ],
    };
  },
  loader: async ({ context, params }) => {
    const article = await context.queryClient.ensureQueryData(articleBySlugQuery(params.slug));
    if (!article) throw notFound();
    return { article };
  },
  notFoundComponent: () => (
    <Section>
      <h1 className="font-display text-4xl text-navy">Story not found</h1>
      <p className="mt-2 text-muted-foreground">That article is offside. Try another headline.</p>
      <Link to="/news" className="mt-4 inline-flex items-center gap-1 font-condensed text-sm font-bold uppercase tracking-widest text-primary">
        <ArrowLeft className="h-4 w-4" /> Back to news
      </Link>
    </Section>
  ),
  component: NewsDetailPage,
});

function NewsDetailPage() {
  const { article } = Route.useLoaderData();
  const { data: all } = useSuspenseQuery(newsByCategoryQuery(undefined, 48));
  const related = all.filter((a) => a.id !== article.id && a.category === article.category).slice(0, 3);
  const img = resolveArticleHeroImage(article);
  const matchOpponent = getArgentinaMatchOpponent(`${article.title} ${article.excerpt ?? ""} ${article.slug ?? ""}`);

  return (
    <>
      <section className="relative overflow-hidden border-b border-border bg-navy text-primary-foreground">
        <div className="absolute inset-x-0 top-0 h-1 sky-bar" />
        <div className="relative mx-auto max-w-3xl px-4 py-12 lg:py-20 lg:px-0">
          <Link to="/news" className="inline-flex items-center gap-1 font-condensed text-xs font-bold uppercase tracking-widest text-sky-deep">
            <ArrowLeft className="h-4 w-4" /> All news
          </Link>
          <p className="mt-4 font-condensed text-xs font-bold uppercase tracking-[0.3em] text-sky-deep">
            {article.category.replace(/_/g, " ")}
          </p>
          <MatchFlags opponent={matchOpponent} className="mt-4" />
          <h1 className="mt-3 font-stadium text-4xl leading-[0.95] md:text-6xl lg:text-7xl">
            {article.title}
          </h1>
          {article.excerpt && (
            <p className="mt-5 text-base text-primary-foreground/85 md:text-lg">{article.excerpt}</p>
          )}
          <div className="mt-6 flex flex-wrap items-center gap-4 font-condensed text-xs font-semibold uppercase tracking-widest text-primary-foreground/70">
            <span className="inline-flex items-center gap-1.5"><User className="h-3.5 w-3.5" /> {article.author ?? "Argentinian.Soccer"}</span>
            <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" /> {dateUtils.fmtMonthDay(article.published_at)}</span>
          </div>
        </div>
      </section>

      <Section>
        <div className="mx-auto max-w-3xl">
          <Img src={img} alt={article.title} width={1280} height={720} priority className="aspect-video w-full rounded-xl object-cover" />
          <div className="prose prose-lg mt-8 max-w-none text-foreground">
            {article.excerpt && (
              <p className="text-lg leading-relaxed text-muted-foreground">
                {article.excerpt}
              </p>
            )}
            {article.body ? (
              article.body
                .split(/\n\n+/)
                .map((para: string, i: number) => {
                  const trimmed = para.trim();
                  // Full-paragraph bold (epigraph): **...**
                  const boldOnly = /^\*\*([\s\S]+)\*\*$/.exec(trimmed);
                  if (boldOnly) {
                    return (
                      <p key={i} className="mt-4 font-display text-xl font-bold leading-snug text-navy">
                        {boldOnly[1]}
                      </p>
                    );
                  }
                  // Full-paragraph italic: *...*
                  const italicOnly = /^\*([^*][\s\S]*?)\*$/.exec(trimmed);
                  if (italicOnly) {
                    return (
                      <p key={i} className="mt-4 italic leading-relaxed text-muted-foreground">
                        {italicOnly[1]}
                      </p>
                    );
                  }
                  // Inline **bold** / *italic*
                  const tokens = trimmed.split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g).filter(Boolean);
                  return (
                    <p key={i} className="mt-4 leading-relaxed">
                      {tokens.map((t, j) => {
                        const b = /^\*\*([\s\S]+)\*\*$/.exec(t);
                        if (b) return <strong key={j}>{b[1]}</strong>;
                        const it = /^\*([\s\S]+)\*$/.exec(t);
                        if (it) return <em key={j}>{it[1]}</em>;
                        return <span key={j}>{t}</span>;
                      })}
                    </p>
                  );
                })
            ) : (
              <>
                <p className="mt-4 leading-relaxed">
                  This is a developing story on Argentinian.Soccer. Our editorial team is updating coverage as new information becomes available. Check back shortly for the full report, post-match reaction, and tactical breakdowns.
                </p>
                <p className="mt-4 leading-relaxed">
                  For more from the {article.category.replace(/_/g, " ")} desk, explore the section below or sign up to get the day's top stories delivered every morning.
                </p>
              </>
            )}
          </div>

        </div>
      </Section>

      {related.length > 0 && (
        <Section eyebrow="More" title="Related stories">
          <div className="grid gap-5 md:grid-cols-3">
            {related.map((a) => (
              <NewsCard key={a.id} article={a} />
            ))}
          </div>
        </Section>
      )}
    </>
  );
}
